import { v4 as uuidv4 } from 'uuid';
import { Position } from '../Transform/Position';
import { Size } from '../Size';
import { ElementPosition } from '../ElementPosition';
import { ElementHandle } from '../ElementHandle';
import { Transform } from '../Transform/Transform';
import { Rectangle } from '../Rectangle';
export class GraphicElement {
    constructor(engine, uuid = GraphicElement.generateUUID(), pos, size, scale, rotation, options) {
        this.referencePosition = new ElementPosition('center');
        this._isLocked = false;
        this._canBeUnlocked = true;
        this.visible = true;
        this._canBeSelected = true;
        this.isDuplicated = true;
        this._isInsideTheme = false;
        this._engine = engine;
        this._uuid = uuid;
        this.transform = new Transform(engine, this._isLocked, !this._isInsideTheme, pos, scale, rotation, () => {
            return new ElementPosition('center').getPosition(this);
        });
        this.originSize = size;
        this.options = options;
        this._handles = {
            tr: new ElementHandle(new ElementPosition('tr'), 'scale', this._uuid, engine),
            tl: new ElementHandle(new ElementPosition('tl'), 'scale', this._uuid, engine),
            bl: new ElementHandle(new ElementPosition('bl'), 'scale', this._uuid, engine),
            br: new ElementHandle(new ElementPosition('br'), 'scale', this._uuid, engine),
            mr: new ElementHandle(new ElementPosition('mr'), 'scale', this._uuid, engine),
            ml: new ElementHandle(new ElementPosition('ml'), 'scale', this._uuid, engine),
            mb: new ElementHandle(new ElementPosition('mb'), 'scale', this._uuid, engine),
            mt: new ElementHandle(new ElementPosition('mt'), 'scale', this._uuid, engine),
            mtr: new ElementHandle(new ElementPosition('mtr'), 'rotate', this._uuid, engine),
            [Symbol.iterator]: function* gen() {
                yield this.tr;
                yield this.tl;
                yield this.bl;
                yield this.br;
                yield this.mr;
                yield this.ml;
                yield this.mb;
                yield this.mt;
                yield this.mtr;
            },
            setSize(size) {
                for (const handle of this) {
                    handle.size = size;
                }
            }
        };
    }
    get engine() {
        return this._engine;
    }
    set engine(engine) {
        this._engine = engine;
        this.transform._engine = engine;
        for (const handle of this._handles) {
            handle._engine = engine;
        }
    }
    getAllGraphicElements() {
        return [this];
    }
    lock() {
        this.isLocked = true;
    }
    unlock() {
        if (this.canBeUnlocked) {
            this.isLocked = false;
        }
    }
    get uuid() {
        return this._uuid;
    }
    set uuid(uuid) {
        this._uuid = uuid;
        for (const handle of this.handles) {
            handle.elementUUID = uuid;
        }
    }
    get isSelected() {
        return this === this._engine.selectedElement;
    }
    get angle() {
        return this.transform.rotation.angle;
    }
    static generateUUID() {
        return uuidv4();
    }
    get pos() {
        return this.transform.pos;
    }
    get localPos() {
        return this.transform.localPos;
    }
    get scale() {
        return this.transform.scale;
    }
    get rotation() {
        return this.transform.rotation;
    }
    get handles() {
        return this._handles;
    }
    get isInsideTheme() {
        return this._isInsideTheme;
    }
    set isInsideTheme(isInsideTheme) {
        this._isInsideTheme = isInsideTheme;
        this.transform.shouldApplyConstraints = !isInsideTheme;
    }
    get size() {
        return new Size(this.originSize.width * this.scale.scaleX, this.originSize.height * this.scale.scaleY);
    }
    get center() {
        return new ElementPosition('center').getPosition(this);
    }
    get left() {
        return Math.min(
            new ElementPosition('tl').getPosition(this).x,
            new ElementPosition('bl').getPosition(this).x,
            new ElementPosition('tr').getPosition(this).x,
            new ElementPosition('br').getPosition(this).x
        );
    }
    get right() {
        return Math.max(
            new ElementPosition('tl').getPosition(this).x,
            new ElementPosition('bl').getPosition(this).x,
            new ElementPosition('tr').getPosition(this).x,
            new ElementPosition('br').getPosition(this).x
        );
    }
    get top() {
        return Math.max(
            new ElementPosition('tl').getPosition(this).y,
            new ElementPosition('bl').getPosition(this).y,
            new ElementPosition('tr').getPosition(this).y,
            new ElementPosition('br').getPosition(this).y
        );
    }
    get bottom() {
        return Math.min(
            new ElementPosition('tl').getPosition(this).y,
            new ElementPosition('bl').getPosition(this).y,
            new ElementPosition('tr').getPosition(this).y,
            new ElementPosition('br').getPosition(this).y
        );
    }
    get leftWithHandles() {
        const array = [];
        for (const handle of this.handles) {
            for (const coord in handle.coords) {
                array.push(handle.coords[coord].x);
            }
        }
        return Math.min(...array);
    }
    get rightWithHandles() {
        const array = [];
        for (const handle of this.handles) {
            for (const coord in handle.coords) {
                array.push(handle.coords[coord].x);
            }
        }
        return Math.max(...array);
    }
    get topWithHandles() {
        const array = [];
        for (const handle of this.handles) {
            for (const coord in handle.coords) {
                array.push(handle.coords[coord].y);
            }
        }
        return Math.min(...array);
    }
    get bottomWithHandles() {
        const array = [];
        for (const handle of this.handles) {
            for (const coord in handle.coords) {
                array.push(handle.coords[coord].y);
            }
        }
        return Math.max(...array);
    }
    get tlWithHandles() {
        return new Position(this.leftWithHandles, this.topWithHandles);
    }
    get trWithHandles() {
        return new Position(this.rightWithHandles, this.topWithHandles);
    }
    get blWithHandles() {
        return new Position(this.leftWithHandles, this.bottomWithHandles);
    }
    get brWithHandles() {
        return new Position(this.rightWithHandles, this.bottomWithHandles);
    }
    unselectHandle() {
        this.selectedHandle = undefined;
    }
    get isLocked() {
        if (!this.canBeUnlocked) {
            return true;
        }
        return this._isLocked;
    }
    set isLocked(isLocked) {
        this._isLocked = isLocked;
        this.transform.isLocked = isLocked;
    }
    mayBeSelectable(pos) {
        const rect = new Rectangle(this.tlWithHandles, this.blWithHandles, this.brWithHandles, this.trWithHandles);
        return rect.isInside(pos);
    }
    isInside(pos) {
        return this.hitbox.isInside(pos);
    }
    getPosition(namePos) {
        return new ElementPosition(namePos).getPosition(this);
    }
    /** return selected handle */
    selectHandle(pos) {
        for (const handle of this.handles) {
            if (handle.isInside(pos)) {
                this.selectedHandle = handle;
                return;
            }
        }
        this.selectedHandle = undefined;
    }
    update(content, options, scale) {
        if (content) {
            this.content = content;
        }
        this.options = Object.assign(Object.assign({}, this.options), options);
        if (scale) {
            this.transform.resize(scale);
        }
    }
    addOrReplaceOptions(options) {
        this.options = Object.assign(Object.assign({}, this.options), options);
    }
    get content() {
        return this._content;
    }
    set content(content) {
        this._content = content;
    }
    get canBeSelected() {
        return !this.isLocked;
    }
    set scale(newScale) {
        this.transform.resize(newScale);
    }
    get hitbox() {
        return new Rectangle(
            this.getPosition('tl'),
            this.getPosition('bl'),
            this.getPosition('br'),
            this.getPosition('tr')
        );
    }
    get canBeUnlocked() {
        return this._canBeUnlocked;
    }
    set canBeUnlocked(canBeUnlocked) {
        this._canBeUnlocked = canBeUnlocked;
    }
}
